/**
 * ArcGIS Pro–style Location / Spectral helpers for the live raster hover.
 *
 * Pure formatters + image-pixel math so the chart UI and tests stay free of
 * React / TiTiler wiring.
 */

import { formatCrsCode } from './rasterMetadata';
import type { TitilerRasterProfile } from '@/utils/titilerRasterProfile';

export type SpectralValueRange = {
  readonly min: number;
  readonly max: number;
};

/** Decimal degrees with hemisphere suffixes — ArcGIS "Decimal" row. */
export function formatDecimalLonLat(lon: number, lat: number): string {
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return '—';
  const ns = lat >= 0 ? 'N' : 'S';
  const ew = lon >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(6)}°${ns} ${Math.abs(lon).toFixed(6)}°${ew}`;
}

/** Pedigree / CRS label for the Location panel. */
export function formatPedigree(
  crs: string | null | undefined,
  fallback?: string | null,
): string {
  return formatCrsCode(crs) ?? formatCrsCode(fallback) ?? fallback?.trim() ?? '—';
}

/**
 * Ground sample distance from native bounds ÷ dimensions.
 * Returns `[gsdX, gsdY]` in CRS units (metres for projected, degrees for geographic).
 */
export function computeGsdMetres(
  profile: Pick<TitilerRasterProfile, 'width' | 'height' | 'bounds' | 'crs'> | null,
): readonly [number, number] | null {
  if (!profile?.width || !profile.height || !profile.bounds) return null;
  if (profile.width <= 0 || profile.height <= 0) return null;
  const [minX, minY, maxX, maxY] = profile.bounds;
  const dx = Math.abs(maxX - minX) / profile.width;
  const dy = Math.abs(maxY - minY) / profile.height;
  if (!Number.isFinite(dx) || !Number.isFinite(dy) || dx <= 0 || dy <= 0) return null;
  return [dx, dy];
}

export function formatGsdPair(
  profile: Pick<TitilerRasterProfile, 'width' | 'height' | 'bounds' | 'crs'> | null,
): string {
  const pair = computeGsdMetres(profile);
  if (!pair) return '—';
  const [dx, dy] = pair;
  const crs = formatCrsCode(profile?.crs) ?? '';
  const geographic = /4326|4269|CRS84|WGS\s*84/i.test(crs)
    || (profile?.bounds != null && Math.abs(profile.bounds[0]) <= 180);
  const fmt = (v: number) => {
    if (geographic) return `${v.toPrecision(4)}°`;
    if (v >= 100) return `${v.toFixed(1)} m`;
    if (v >= 1) return `${v.toFixed(0)} m`;
    if (v >= 0.01) return `${v.toFixed(2)} m`;
    return `${v.toPrecision(3)} m`;
  };
  return `${fmt(dx)}, ${fmt(dy)}`;
}

/**
 * Map WGS84 lon/lat onto image column/row when the COG CRS is geographic
 * (or bounds already look like lon/lat). Projected CRS without a transform
 * returns null — the UI shows "—".
 */
export function lonLatToImagePixel(
  lon: number,
  lat: number,
  profile: Pick<TitilerRasterProfile, 'width' | 'height' | 'bounds' | 'crs'> | null,
  projectedXY?: readonly [number, number] | null,
): { col: number; row: number } | null {
  if (!profile?.width || !profile.height || !profile.bounds) return null;
  const [minX, minY, maxX, maxY] = profile.bounds;
  const crs = formatCrsCode(profile.crs) ?? '';
  const geographic = /4326|4269|CRS84|WGS\s*84/i.test(crs)
    || (Math.abs(minX) <= 180 && Math.abs(maxX) <= 180);

  let x = lon;
  let y = lat;
  if (!geographic) {
    if (!projectedXY || !Number.isFinite(projectedXY[0]) || !Number.isFinite(projectedXY[1])) {
      return null;
    }
    x = projectedXY[0];
    y = projectedXY[1];
  }

  const widthWorld = maxX - minX;
  const heightWorld = maxY - minY;
  if (!Number.isFinite(widthWorld) || !Number.isFinite(heightWorld)) return null;
  if (Math.abs(widthWorld) < 1e-12 || Math.abs(heightWorld) < 1e-12) return null;

  const col = ((x - minX) / widthWorld) * profile.width;
  // North-up: row 0 at the top (maxY).
  const row = ((maxY - y) / heightWorld) * profile.height;
  if (!Number.isFinite(col) || !Number.isFinite(row)) return null;
  if (col < -0.5 || row < -0.5 || col > profile.width + 0.5 || row > profile.height + 0.5) {
    return null;
  }
  return { col, row };
}

export function formatImagePixel(col: number | null, row: number | null): string {
  if (col == null || row == null || !Number.isFinite(col) || !Number.isFinite(row)) {
    return '—';
  }
  return `${Math.round(col)}, ${Math.round(row)}`;
}

/**
 * Y-axis range for the Spectral bar chart. Prefers stretch / symbology
 * stats; falls back to the live sample so a bar still has somewhere to grow.
 */
export function resolveSpectralRange(
  values: readonly number[],
  preferredMin: number | null | undefined,
  preferredMax: number | null | undefined,
): SpectralValueRange {
  const finite = values.filter((v) => Number.isFinite(v));
  let min = typeof preferredMin === 'number' && Number.isFinite(preferredMin)
    ? preferredMin
    : 0;
  let max = typeof preferredMax === 'number' && Number.isFinite(preferredMax)
    ? preferredMax
    : Math.max(...finite, 1);

  if (finite.length > 0) {
    const sampleMin = Math.min(...finite);
    const sampleMax = Math.max(...finite);
    if (sampleMin < min) min = sampleMin;
    if (sampleMax > max) max = sampleMax;
  }

  if (!(max > min)) {
    max = min + 1;
  }
  return { min, max };
}

/** 0..1 bar fill fraction for a sample value against the chart range. */
export function spectralBarFraction(
  value: number,
  range: SpectralValueRange,
): number {
  if (!Number.isFinite(value)) return 0;
  const span = range.max - range.min;
  if (!(span > 0)) return 0;
  return Math.max(0, Math.min(1, (value - range.min) / span));
}

export function formatSpectralAxisTick(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (Math.abs(value) >= 1000) return value.toFixed(0);
  if (Math.abs(value) >= 10) return value.toFixed(0);
  if (Math.abs(value) >= 1) return value.toFixed(1);
  return value.toPrecision(3);
}
